/**
 * BOARD interior — the room behind the BOARD building (plot P1, domain 'board').
 *
 * Pure layout + status logic, no PlayCanvas dependency (so it is unit-testable
 * without a graphics device). The visible room, the card meshes, the DOM cards
 * and the [E] handling live in boardSystem.ts, which consumes these.
 *
 * The room is a plain rectangular shell (halfX 14, halfZ 8, 3 m ceiling) with a
 * KANBAN WALL on its north face: five column bays — READY / IN-PROGRESS /
 * BLOCKED / DONE / CANCELLED — each a banner above a downward stack of task
 * cards. Every task in the org backlog is one card, placed in its status column.
 *
 * Coordinates are room-LOCAL (origin-centred), exactly like the office
 * interior; the island offset is applied by the boardRoot entity transform.
 */
import {
  WALL_HEIGHT, WALL_THICKNESS, ENTRY_DOOR_HALF_WIDTH, ENTRY_DOOR_HEIGHT,
  type WallSegment,
} from './layout'
import type { Task, TaskStatus } from '../../api/types'

// ── Room shell ──────────────────────────────────────────────────────────────

export const BOARD_HALF_X = 14
export const BOARD_HALF_Z = 8
export const BOARD_WALL_HEIGHT = WALL_HEIGHT // 3 — shared human-scale datum

/** Inner faces of the exterior walls (usable floor area). */
export const BOARD_INNER_HALF_X = BOARD_HALF_X - WALL_THICKNESS / 2
export const BOARD_INNER_HALF_Z = BOARD_HALF_Z - WALL_THICKNESS / 2

/** Authored glass strategy suite; shared by render, collision and acceptance. */
export const BOARD_STRATEGY_ROOM = {
  centerX: 0,
  centerZ: 1.2,
  width: 10.5,
  depth: 5.8,
} as const

// ── Kanban columns ────────────────────────────────────────────────────────────
// Five status columns, displayed west→east. The board plane sits just inside
// the north wall; cards stack downward from BOARD_CARD_TOP_Y per column.

/** Column order across the wall (west→east). The card's status picks its column. */
export const BOARD_COLUMNS: readonly TaskStatus[] = [
  'ready', 'in-progress', 'blocked', 'done', 'cancelled',
]

/** Human-readable banner text per column. */
export const BOARD_COLUMN_LABELS: Record<TaskStatus, string> = {
  'ready': 'READY',
  'in-progress': 'IN-PROGRESS',
  'blocked': 'BLOCKED',
  'done': 'DONE',
  'cancelled': 'CANCELLED',
}

/** Board plane Z (local): just inside the north wall. */
export const BOARD_WALL_Z = -BOARD_HALF_Z + 0.4 // -7.6

export const BOARD_COL_MIN_X = -12
export const BOARD_COL_MAX_X = 12
export const BOARD_COL_PITCH = (BOARD_COL_MAX_X - BOARD_COL_MIN_X) / BOARD_COLUMNS.length // 4.8

/** How many cards fit in a column before the stack is capped. */
export const BOARD_CARDS_PER_COLUMN = 5

export const BOARD_CARD_W = 3.6
export const BOARD_CARD_H = 0.44
/** Card-centre Y of the top card in a stack, and the vertical pitch downward. */
export const BOARD_CARD_TOP_Y = 2.2
export const BOARD_CARD_PITCH = 0.48

/** Column banner geometry (canvas-textured sign above each stack). */
export const BOARD_BANNER_Y = 2.74
export const BOARD_BANNER_W = 4.4
export const BOARD_BANNER_H = 0.42

/** Centre X of a column bay. */
export function boardColumnX(col: number): number {
  return BOARD_COL_MIN_X + (col + 0.5) * BOARD_COL_PITCH
}

/** Banner-centre position for a column (local coords). */
export function boardBannerPosition(col: number): [number, number, number] {
  return [boardColumnX(col), BOARD_BANNER_Y, BOARD_WALL_Z]
}

/** Card-centre position for a (column, stack-row) slot (local coords). */
export function boardCardPosition(col: number, row: number): [number, number, number] {
  return [boardColumnX(col), BOARD_CARD_TOP_Y - row * BOARD_CARD_PITCH, BOARD_WALL_Z]
}

// ── Task status ───────────────────────────────────────────────────────────────

/**
 * Map any raw portal status string onto one of the five board columns. The
 * portal writes 'ready' / 'in-progress' / 'blocked' / 'done' / 'cancelled', but
 * older rows and aliases (todo, in_progress, completed, canceled…) show up too.
 * Anything unrecognised lands in READY so no task is ever dropped off the wall.
 */
export function normalizeTaskStatus(raw: string | null | undefined): TaskStatus {
  const s = (raw ?? '').toLowerCase().trim().replace(/_/g, '-')
  switch (s) {
    case 'ready':
    case 'todo':
    case 'backlog':
    case 'open':
    case 'new':
      return 'ready'
    case 'in-progress':
    case 'inprogress':
    case 'doing':
    case 'active':
    case 'running':
    case 'started':
      return 'in-progress'
    case 'blocked':
    case 'waiting':
    case 'on-hold':
      return 'blocked'
    case 'done':
    case 'completed':
    case 'complete':
    case 'closed':
    case 'finished':
      return 'done'
    case 'cancelled':
    case 'canceled':
    case 'wontfix':
    case 'abandoned':
      return 'cancelled'
    default:
      return 'ready'
  }
}

/**
 * Forward transition [E] applies to a card. This is the sensible kanban flow,
 * not a blind march through the display columns: a READY card starts, an
 * IN-PROGRESS card completes, a BLOCKED card un-blocks back to IN-PROGRESS.
 * The two terminal columns (DONE, CANCELLED) have no next — [E] is a no-op there.
 */
const STATUS_ADVANCE: Record<TaskStatus, TaskStatus | null> = {
  'ready': 'in-progress',
  'in-progress': 'done',
  'blocked': 'in-progress',
  'done': null,
  'cancelled': null,
}

export function nextTaskStatus(status: TaskStatus): TaskStatus | null {
  return STATUS_ADVANCE[status]
}

// ── Card layout ───────────────────────────────────────────────────────────────

/** Priority → inner-panel colour ([r,g,b] 0-1). Mirrors the station palette. */
export const BOARD_PRIORITY_COLORS: Record<string, [number, number, number]> = {
  p0:       [0.973, 0.443, 0.443], // red
  critical: [0.973, 0.443, 0.443],
  p1:       [1.000, 0.650, 0.150], // amber
  high:     [1.000, 0.650, 0.150],
  p2:       [0.133, 0.827, 0.933], // cyan
  medium:   [0.133, 0.827, 0.933],
  p3:       [0.580, 0.639, 0.722], // slate
  low:      [0.580, 0.639, 0.722],
}

export const BOARD_PRIORITY_DEFAULT: [number, number, number] = [0.580, 0.639, 0.722]

export function boardPriorityColor(priority?: string): [number, number, number] {
  if (!priority) return BOARD_PRIORITY_DEFAULT
  return BOARD_PRIORITY_COLORS[priority.toLowerCase()] ?? BOARD_PRIORITY_DEFAULT
}

/** A task placed on the wall — consumed by the board system for meshes + labels. */
export interface BoardCard {
  taskId: string
  /** Column index into BOARD_COLUMNS (i.e. the card's status column). */
  col: number
  /** Stack row within the column (0 = top). */
  row: number
  /** Card-centre position, room-local. */
  x: number
  y: number
  z: number
  status: TaskStatus
  priority?: string
  /** True when the card is in the BLOCKED column — gets a red corner tag. */
  blocked: boolean
}

/**
 * Place every task on the wall: bucket by status column, then stack downward in
 * input order, capped at BOARD_CARDS_PER_COLUMN per column. Pure — the board
 * system rebuilds meshes from this whenever task data changes.
 */
export function layoutBoardCards(tasks: readonly Task[]): BoardCard[] {
  const buckets = new Map<TaskStatus, Task[]>()
  for (const status of BOARD_COLUMNS) buckets.set(status, [])
  for (const t of tasks) {
    buckets.get(normalizeTaskStatus(t.status))!.push(t)
  }

  const cards: BoardCard[] = []
  BOARD_COLUMNS.forEach((status, col) => {
    const list = buckets.get(status)!
    for (let row = 0; row < Math.min(list.length, BOARD_CARDS_PER_COLUMN); row++) {
      const t = list[row]
      const [x, y, z] = boardCardPosition(col, row)
      cards.push({
        taskId: t.id,
        col,
        row,
        x, y, z,
        status,
        priority: t.priority,
        blocked: status === 'blocked',
      })
    }
  })
  return cards
}

export interface BoardLaunchCandidate {
  taskId: string
  canLaunch: boolean
  worldDistance: number
  screenX: number
  screenY: number
  commandStatus?: 'pending' | 'accepted' | 'failed'
}

/**
 * Select the authorized card under the reticle. World distance alone cannot
 * distinguish vertically stacked cards because a column shares X/Z.
 */
export function selectBoardLaunchTarget(
  candidates: readonly BoardLaunchCandidate[],
  viewportWidth: number,
  viewportHeight: number,
  maxWorldDistance = 2.8,
  maxReticleDistance = 180,
): string | null {
  const centerX = viewportWidth / 2
  const centerY = viewportHeight / 2
  let selected: { taskId: string; aim: number; world: number } | null = null
  for (const candidate of candidates) {
    if (
      !candidate.canLaunch ||
      candidate.worldDistance >= maxWorldDistance ||
      candidate.commandStatus === 'pending' ||
      candidate.commandStatus === 'accepted'
    ) continue
    const aim = Math.hypot(candidate.screenX - centerX, candidate.screenY - centerY)
    if (aim > maxReticleDistance) continue
    if (
      !selected ||
      aim < selected.aim ||
      (aim === selected.aim && candidate.worldDistance < selected.world)
    ) selected = { taskId: candidate.taskId, aim, world: candidate.worldDistance }
  }
  return selected?.taskId ?? null
}

// ── Room walls ────────────────────────────────────────────────────────────────

function wallX(id: string, x: number, minZ: number, maxZ: number): WallSegment {
  return {
    id, minX: x - WALL_THICKNESS / 2, maxX: x + WALL_THICKNESS / 2,
    minZ, maxZ, minY: 0, maxY: BOARD_WALL_HEIGHT, material: 'cream',
  }
}

function wallZ(id: string, z: number, minX: number, maxX: number): WallSegment {
  return {
    id, minX, maxX, minZ: z - WALL_THICKNESS / 2, maxZ: z + WALL_THICKNESS / 2,
    minY: 0, maxY: BOARD_WALL_HEIGHT, material: 'cream',
  }
}

/**
 * Every wall of the board room: a solid shell with a south entry gap
 * (x ∈ ±ENTRY_DOOR_HALF_WIDTH) closed above ENTRY_DOOR_HEIGHT by a lintel.
 * Door gaps are expressed as missing segments, so colliders inherit them.
 */
export function computeBoardWalls(): WallSegment[] {
  return [
    wallZ('board-wall-north', -BOARD_HALF_Z, -BOARD_HALF_X, BOARD_HALF_X),
    wallZ('board-wall-south-west', BOARD_HALF_Z, -BOARD_HALF_X, -ENTRY_DOOR_HALF_WIDTH),
    wallZ('board-wall-south-east', BOARD_HALF_Z, ENTRY_DOOR_HALF_WIDTH, BOARD_HALF_X),
    // Lintel closes the wall above the door opening.
    {
      ...wallZ('board-entry-lintel', BOARD_HALF_Z, -ENTRY_DOOR_HALF_WIDTH, ENTRY_DOOR_HALF_WIDTH),
      minY: ENTRY_DOOR_HEIGHT,
    },
    wallX('board-wall-west', -BOARD_HALF_X, -BOARD_HALF_Z, BOARD_HALF_Z),
    wallX('board-wall-east', BOARD_HALF_X, -BOARD_HALF_Z, BOARD_HALF_Z),
  ]
}
